import { Controller } from "@hotwired/stimulus"

// Campaign editor: shows the trigger sub-fields, the locked audience chips and
// the template variables matching the selected trigger type, and reloads the two live-feedback Turbo
// Frames independently so a filter change never re-renders the preview and a
// template change never re-runs the audience query.
export default class extends Controller {
  static targets = ["triggerType", "triggerGroup", "lockedFilterSet", "variableSet", "audienceCheckbox", "templateSelect", "localePanel"]

  static values = { estimateUrl: String, previewUrl: String }

  static DEBOUNCE_MS = 300

  connect() {
    this.syncTriggerGroups()
    this.syncAudienceKindSets()
    this.audienceKind = this.hasTriggerTypeTarget ? this.selectedAudienceKind() : null
  }

  disconnect() {
    clearTimeout(this.estimateTimeout)
  }

  // A type switch can change which table the audience is counted against
  // (users vs invitations), so the estimate reloads too. The rendered preview
  // uses that kind's sample payload, so it reloads only when the kind changes.
  triggerTypeChanged() {
    this.syncTriggerGroups()
    this.syncAudienceKindSets()
    this.scheduleEstimateReload()

    const kind = this.selectedAudienceKind()
    if (kind !== this.audienceKind) {
      this.audienceKind = kind
      if (this.hasTemplateSelectTarget && this.templateSelectTarget.value) this.templateChanged()
    }
  }

  // Hidden groups are disabled as well as hidden: several trigger types share a
  // field name, so an enabled-but-hidden input would post alongside the visible
  // one and the last value would win.
  syncTriggerGroups() {
    if (!this.hasTriggerTypeTarget) return
    const selected = this.triggerTypeTarget.value

    this.triggerGroupTargets.forEach((group) => {
      const matches = group.dataset.triggerType === selected
      group.classList.toggle("hidden", !matches)
      group.querySelectorAll("input, select").forEach((field) => {
        field.disabled = !matches
      })
    })
  }

  // The locked chips describe baseline clauses and the variable sets describe
  // payloads, and both differ by audience kind. The kind map comes from the
  // server (data-audience-kinds on the select), so this file never restates
  // which trigger types address invitations.
  syncAudienceKindSets() {
    if (!this.hasTriggerTypeTarget) return
    const kind = this.selectedAudienceKind()

    ;[ ...this.lockedFilterSetTargets, ...this.variableSetTargets ].forEach((set) => {
      set.classList.toggle("hidden", set.dataset.audienceKind !== kind)
    })
  }

  selectedAudienceKind() {
    let kinds = {}
    try {
      kinds = JSON.parse(this.triggerTypeTarget.dataset.audienceKinds || "{}")
    } catch {
      kinds = {}
    }
    return kinds[this.triggerTypeTarget.value] || "users"
  }

  audienceChanged() {
    this.scheduleEstimateReload()
  }

  scheduleEstimateReload() {
    clearTimeout(this.estimateTimeout)
    this.estimateTimeout = setTimeout(() => this.reloadEstimate(), this.constructor.DEBOUNCE_MS)
  }

  reloadEstimate() {
    const frame = document.getElementById("lifecycle_audience_estimate")
    if (!frame) return

    const params = new URLSearchParams()
    this.audienceCheckboxTargets
      .filter((box) => box.checked)
      .forEach((box) => params.append("audience_filters[]", box.value))
    if (this.hasTriggerTypeTarget) params.append("trigger_type", this.triggerTypeTarget.value)

    frame.src = `${this.estimateUrlValue}?${params.toString()}`
  }

  templateChanged() {
    const frame = document.getElementById("lifecycle_template_preview")
    if (!frame || !this.hasTemplateSelectTarget) return

    const params = new URLSearchParams({ template_alias: this.templateSelectTarget.value })
    if (this.hasTriggerTypeTarget) params.append("trigger_type", this.triggerTypeTarget.value)
    frame.src = `${this.previewUrlValue}?${params.toString()}`
  }

  showLocale(event) {
    const locale = event.currentTarget.dataset.locale

    this.localePanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.locale !== locale)
    })

    this.element.querySelectorAll("[data-action~='lifecycle-campaign-form#showLocale']").forEach((tab) => {
      const active = tab.dataset.locale === locale
      tab.classList.toggle("text-gray-900", active)
      tab.classList.toggle("border-b-2", active)
      tab.classList.toggle("border-frankly-red-500", active)
      tab.classList.toggle("text-gray-400", !active)
    })
  }
}
